import { api } from './api';
import { formatPhoneForWhatsApp, openWhatsAppChat } from './formatPhoneForWhatsApp';

export { openWhatsAppChat, formatPhoneForWhatsApp } from './formatPhoneForWhatsApp';

// Template types
export const TEMPLATE_TYPES = {
  MAINTENANCE_REGISTRATION: 'maintenanceRegistration',
  MAINTENANCE_COMPLETION: 'maintenanceCompletion',
  PICKUP_REMINDER: 'pickupReminder',
  ESTIMATE_READY: 'estimateReady',
  MAINTENANCE_REVIEW: 'maintenanceReview'
};

// Default templates in case API call fails
const DEFAULT_TEMPLATES = {
  maintenanceRegistration: 'Salve *{customerName}*, la informiamo che la sua richiesta di manutenzione è stata registrata con il numero di protocollo *{protocolNumber}*. Veicolo: *{vehicleModel}*. Problema: *{problemDescription}*. Acconto versato: *{deposit}*€. Per i termini di servizio: *{termsLink}*. La contatteremo appena sarà pronta.',
  maintenanceCompletion: 'Salve *{customerName}*, la informiamo che la sua bicicletta è pronta per il ritiro. Protocollo: *{protocolNumber}*. Veicolo: *{vehicleModel}*. Importo totale: *{totalAmount}*€ (Acconto versato: *{deposit}*€). Grazie!',
  pickupReminder: 'Salve *{customerName}*, le ricordiamo che la sua bicicletta (protocollo: *{protocolNumber}*, modello: *{vehicleModel}*) è pronta per il ritiro da *{daysInShop}* giorni. Importo da saldare: *{totalAmount}*€ (Acconto versato: *{deposit}*€). La preghiamo di passare a ritirarla al più presto. Per i termini di servizio: *{termsLink}*. Grazie!',
  estimateReady: 'Salve *{customerName}*, il preventivo per la sua bicicletta (protocollo: *{protocolNumber}*, modello: *{vehicleModel}*) è pronto. Il costo stimato è di *{estimateAmount}*€. Acconto versato: *{deposit}*€. La preghiamo di contattarci per confermare o per eventuali chiarimenti.',
  maintenanceReview: 'Salve *{customerName}*, grazie per aver scelto il nostro servizio! Se desidera lasciarci una recensione, può farlo qui: *{reviewLink}*. A presto!'
};

// Cache for templates to avoid repeated API calls
let templatesCache = {};
let settingsCache = {};
let lastFetchTime = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Imposta l'event listener per invalidare la cache quando un template viene aggiornato
if (typeof window !== 'undefined') {
  window.addEventListener('whatsappTemplateUpdated', () => {
    console.log('Template WhatsApp aggiornato, invalidazione cache');
    templatesCache = {}; // Invalida la cache
    settingsCache = {}; // Invalida la cache delle impostazioni
    lastFetchTime = {}; // Resetta i tempi di fetch
  });
}

/**
 * Fetch all WhatsApp templates from the server
 * @returns {Promise<Object>} The templates object
 */
export const fetchTemplates = async () => {
  const companyId = localStorage.getItem('companyId');
  if (!companyId) {
    console.error('CompanyId not found in localStorage');
    return DEFAULT_TEMPLATES;
  }

  const now = Date.now();
  
  // Return cached templates if they're still valid
  if (templatesCache[companyId] && (now - lastFetchTime[companyId] < CACHE_DURATION)) {
    return templatesCache[companyId];
  }
  
  try {
    const res = await api.get('/company-settings', {
      headers: {
        'x-company-id': companyId
      }
    });

    const settings = res.data;
    const templates = {};
    
    // Extract WhatsApp templates from settings
    Object.keys(DEFAULT_TEMPLATES).forEach(key => {
      const settingKey = `whatsappTemplate_${key}`;
      templates[key] = settings && settings[settingKey] 
        ? settings[settingKey] 
        : DEFAULT_TEMPLATES[key];
    });
    
    // Update cache
    templatesCache[companyId] = templates;
    settingsCache[companyId] = settings || {};
    lastFetchTime[companyId] = now;
    
    return templates;
  } catch (err) {
    console.error('Error fetching WhatsApp templates:', err);
    return DEFAULT_TEMPLATES;
  }
};

/**
 * Replace template variables with actual values
 * @param {string} template - The template string with variables
 * @param {Object} data - Object containing values for the variables
 * @returns {string} The processed message with variables replaced
 */
export const processTemplate = (template, data) => {
  if (!template) return '';
  
  let processedTemplate = template;
  
  // Format monetary values
  const monetaryFields = ['deposit', 'totalAmount', 'estimateAmount'];
  const enhancedData = { ...data };
  
  
  // Aggiungiamo valore predefinito per deposit se non presente
  if (!enhancedData.deposit) {
    enhancedData.deposit = '0.00';
  }

  // Valore predefinito per daysInShop
  if (!enhancedData.daysInShop && enhancedData.daysInShop !== 0) {
    enhancedData.daysInShop = '0';
  }
  
  monetaryFields.forEach(field => {
    if (enhancedData[field]) {
      enhancedData[field] = parseFloat(enhancedData[field]).toFixed(2);
    }
  });
  
  console.log('Elaborazione del template con i dati:', enhancedData);
  
  // Replace all variables in the template
  Object.entries(enhancedData).forEach(([key, value]) => {
    // Sostituisci variabili con e senza asterischi
    const patterns = [
      new RegExp(`\\*{${key}}\\*`, 'g'), // *{key}*
      new RegExp(`\\*{${key}}`, 'g'),   // *{key}
      new RegExp(`{${key}}\\*`, 'g'),   // {key}*
      new RegExp(`{${key}}`, 'g')       // {key}
    ];
    
    // Non mettere in grassetto i link: WhatsApp spesso non li rende cliccabili se circondati da '*'
    const isLinkField = key === 'termsLink' || key === 'reviewLink';
    const replacementValue = value ? (isLinkField ? `${value}` : `*${value}*`) : ''; 
    
    for (const pattern of patterns) {
      processedTemplate = processedTemplate.replace(pattern, replacementValue);
    }
  });
  
  console.log('Template elaborato:', processedTemplate);
  
  return processedTemplate;
};

/**
 * Get a specific template and process it with the provided data
 * @param {string} templateType - The type of template to get (use TEMPLATE_TYPES constants)
 * @param {Object} data - Data to replace variables in the template
 * @returns {Promise<string>} The processed message
 */
export const getProcessedTemplate = async (templateType, data) => {
  // 1) Carica settings (template + termsAndConditions)
  const companyId = localStorage.getItem('companyId');
  let settings = {};
  let generalSettings = {};
  
  if (companyId) {
    // Verifica se abbiamo già i settings in cache
    if (settingsCache[companyId] && (Date.now() - lastFetchTime[companyId] < CACHE_DURATION)) {
      settings = settingsCache[companyId];
    } else {
      try {
        const res = await api.get('/company-settings', {
          headers: { 'x-company-id': companyId }
        });
        settings = res.data || {};
        settingsCache[companyId] = settings;
        lastFetchTime[companyId] = Date.now();
      } catch (err) {
        console.error('Error fetching company settings:', err);
      }
    }
  }

  try {
    const gsRes = await api.get('/settings');
    generalSettings = gsRes.data || {};
  } catch (err) {
    console.error('Error fetching general settings:', err);
  }
  
  // 2) Scegli il template giusto dal settings o dai default
  const templates = {};
  Object.keys(DEFAULT_TEMPLATES).forEach(key => {
    const settingKey = `whatsappTemplate_${key}`;
    templates[key] = settings[settingKey] ?? DEFAULT_TEMPLATES[key];
  });
  
  const template = templates[templateType];
  
  // 3) Inietta terms e link recensione (da Impostazioni azienda + company-settings)
  const mergedData = {
    ...data,
    termsLink: data.termsLink || generalSettings.termsAndConditions || settings.termsAndConditions || '',
    reviewLink: data.reviewLink || generalSettings.reviewLink || settings.reviewLink || ''
  };
  
  console.log('Usando termsLink:', settings.termsAndConditions);
  
  return processTemplate(template, mergedData);
};

/**
 * Open WhatsApp with the specified template and data
 * @param {string} phone - The phone number to send the message to
 * @param {string} templateType - The type of template to use
 * @param {Object} data - Data to replace variables in the template
 * @param {string} [countryCode] - Prefisso nazionale senza + (default 39)
 * @returns {Promise<string>} The URL that was opened
 */
export const openWhatsAppWithTemplate = async (phone, templateType, data, countryCode) => {
  if (!phone) {
    console.error('Numero di telefono mancante');
    return null;
  }

  const cleanPhone = formatPhoneForWhatsApp(phone, countryCode);
  if (!cleanPhone) {
    console.error('Numero di telefono non valido');
    return null;
  }
  
  // Get and process the template
  const message = await getProcessedTemplate(templateType, data);
  
  // Create and open the WhatsApp URL
  const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, '_blank');
  
  return whatsappUrl;
};

/**
 * Apre WhatsApp con il template recensioni dopo consegna manutenzione (o da UI).
 * Ricarica la manutenzione se mancano i dati cliente.
 */
export const openMaintenanceReviewWhatsApp = async (maintenance) => {
  if (!maintenance?.id && !maintenance?.Customer?.phone) return;
  let m = maintenance;
  if ((!m?.Customer || !m.Customer.phone) && m?.id) {
    try {
      const res = await api.get(`/maintenance/${m.id}`);
      m = res.data;
    } catch (e) {
      console.error('openMaintenanceReviewWhatsApp: fetch maintenance failed', e);
      return;
    }
  }
  if (!m?.Customer?.phone) return;

  const generalRes = await api.get('/settings').catch(() => ({ data: {} }));
  const general = generalRes.data || {};
  const finalCost = parseFloat(m.finalCost || 0);
  const discount = parseFloat(m.discount || 0);
  const totalAmountWithDiscount = finalCost - discount;

  const templateData = {
    customerName: [m.Customer.firstName, m.Customer.lastName].filter(Boolean).join(' ').trim() || m.Customer.firstName || '',
    protocolNumber: m.protocolNumber != null ? String(m.protocolNumber) : '',
    vehicleModel: m.Vehicle?.model || '',
    problemDescription: m.description || '',
    totalAmount: totalAmountWithDiscount > 0 ? String(totalAmountWithDiscount) : String(m.estimatedCost || ''),
    estimateAmount: String(m.estimatedCost || ''),
    deposit: String(m.deposit ?? '0'),
    termsLink: general.termsAndConditions || '',
    reviewLink: general.reviewLink || '',
    daysInShop: '0'
  };

  await openWhatsAppWithTemplate(
    m.Customer.phone,
    TEMPLATE_TYPES.MAINTENANCE_REVIEW,
    templateData,
    m.Customer.countryCode
  );
};

/**
 * Dati template recensioni per una vendita (storico vendite).
 */
export const buildSaleReviewTemplateData = (sale, generalSettings = {}) => {
  const c = sale?.Customer;
  return {
    customerName: c ? [c.firstName, c.lastName].filter(Boolean).join(' ').trim() : '',
    protocolNumber: sale?.saleNumber != null ? String(sale.saleNumber) : `Vendita ${sale?.id ?? ''}`,
    vehicleModel: '',
    problemDescription: '',
    totalAmount: sale?.totalAmount != null ? String(sale.totalAmount) : '',
    estimateAmount: '',
    deposit: '0',
    termsLink: generalSettings.termsAndConditions || '',
    reviewLink: generalSettings.reviewLink || '',
    daysInShop: '0'
  };
};